import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import {
  areaDisplayName,
  clearActiveAttemptId,
  emptyMockState,
  formatExamTimestamp,
  formatPercentage,
  formatRemaining,
  isAttemptResumable,
  loadActiveAttemptId,
  remainingMilliseconds,
  saveActiveAttemptId,
  saveMockState,
  shortfallLine
} from '../mock-state';
import {
  MockAttempt,
  MockAvailability,
  MockDuration,
  MockHistoryItem,
  MockPaper,
  MockShortfall
} from '../mock.models';
import { MockService } from '../mock.service';
import { isSubscriptionRequired, subscriptionBlocksMock } from '../mock-subscription';

interface ShortfallNote {
  paper: MockPaper;
  item: MockShortfall;
}

@Component({
  selector: 'app-mock-landing',
  templateUrl: './mock-landing.component.html',
  styleUrls: ['./mock-landing.component.scss']
})
export class MockLandingComponent implements OnInit, OnDestroy {
  isSpinner = true;
  selectedPaper: MockPaper | null = null;
  duration: MockDuration = 120;
  paperError = false;
  startError = false;
  subscriptionRequired = false;
  starting = false;
  history: MockHistoryItem[] = [];
  shortfalls: ShortfallNote[] = [];
  resume: MockAttempt | null = null;
  readonly topicKeysA = ['mockPaperATopic1', 'mockPaperATopic2', 'mockPaperATopic3'];
  readonly topicKeysB = [
    'mockPaperBTopic1',
    'mockPaperBTopic2',
    'mockPaperBTopic3',
    'mockPaperBTopic4',
    'mockPaperBTopic5',
    'mockPaperBTopic6'
  ];

  private destroyed = new Subject<void>();
  private loaded = 0;

  constructor(
    private mockService: MockService,
    private router: Router,
    private translate: TranslatePipe
  ) {}

  ngOnInit(): void {
    this.mockService.getProfile().pipe(takeUntil(this.destroyed)).subscribe(profile => {
      if (subscriptionBlocksMock(profile)) {
        this.subscriptionRequired = true;
      }
      this.finishLoad();
    }, () => this.finishLoad());

    this.mockService.getAvailability().pipe(takeUntil(this.destroyed)).subscribe(availability => {
      this.shortfalls = this.readShortfalls(availability);
      this.finishLoad();
    }, error => this.failLoad(error));

    this.mockService.listAttempts().pipe(takeUntil(this.destroyed)).subscribe(items => {
      this.history = (items || []).filter(item => !!item.submitted_at);
      this.finishLoad();
    }, error => this.failLoad(error));

    this.loadResume();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  selectPaper(paper: MockPaper): void {
    this.selectedPaper = paper;
    this.paperError = false;
  }

  selectDuration(duration: MockDuration): void {
    this.duration = duration;
  }

  start(): void {
    if (!this.selectedPaper || this.starting) {
      this.paperError = !this.selectedPaper;
      return;
    }
    this.starting = true;
    this.startError = false;
    const paper = this.selectedPaper;
    this.mockService.createAttempt(paper, this.duration).pipe(takeUntil(this.destroyed)).subscribe(attempt => {
      const id = String(attempt.id);
      saveActiveAttemptId(id);
      saveMockState(id, emptyMockState());
      this.router.navigate(['/mock', id]);
    }, error => {
      this.starting = false;
      if (isSubscriptionRequired(error)) {
        this.subscriptionRequired = true;
        this.startError = false;
        return;
      }
      this.startError = true;
    });
  }

  resumeExam(): void {
    if (!this.resume) {
      return;
    }
    this.router.navigate(['/mock', this.resume.id]);
  }

  openResult(item: MockHistoryItem): void {
    this.router.navigate(['/mock', item.id, 'result']);
  }

  shortfallText(note: ShortfallNote): string {
    const area = areaDisplayName(note.item.area, key => this.translate.transform(key));
    return shortfallLine(
      this.translate.transform('mockShortfallLine'),
      note.paper,
      area,
      note.item.available,
      note.item.requested
    );
  }

  when(iso: string | null): string {
    return formatExamTimestamp(iso);
  }

  scoreText(item: MockHistoryItem): string {
    return item.score + ' / ' + item.max_score;
  }

  percentText(value: number): string {
    return formatPercentage(value, localStorage.getItem('language') || 'de');
  }

  resumeRemaining(): string {
    if (!this.resume) {
      return '';
    }
    return formatRemaining(remainingMilliseconds(this.resume.expires_at, Date.now()));
  }

  private loadResume(): void {
    const id = loadActiveAttemptId();
    if (!id) {
      this.finishLoad();
      return;
    }
    this.mockService.getAttempt(id).pipe(takeUntil(this.destroyed)).subscribe(attempt => {
      if (isAttemptResumable(attempt, Date.now())) {
        this.resume = attempt;
      } else {
        clearActiveAttemptId();
      }
      this.finishLoad();
    }, error => {
      if (isSubscriptionRequired(error)) {
        this.subscriptionRequired = true;
      } else {
        clearActiveAttemptId();
      }
      this.finishLoad();
    });
  }

  private readShortfalls(availability: MockAvailability): ShortfallNote[] {
    const notes: ShortfallNote[] = [];
    (['A', 'B'] as MockPaper[]).forEach(paper => {
      const paperAvailability = availability && availability[paper];
      const items = paperAvailability && paperAvailability.shortfall ? paperAvailability.shortfall : [];
      items.forEach(item => {
        if (item && item.requested > item.available) {
          notes.push({ paper: paper, item: item });
        }
      });
    });
    return notes;
  }

  private failLoad(error: any): void {
    if (isSubscriptionRequired(error)) {
      this.subscriptionRequired = true;
    }
    this.finishLoad();
  }

  private finishLoad(): void {
    this.loaded++;
    if (this.loaded >= 4) {
      this.isSpinner = false;
    }
  }
}
